import React, { useEffect, useMemo, useState } from "react";
import { formatToTwoDecimalPlaces } from "helper/Functions";
import UserService from "../../../service/UserService";
import { API_URL } from "helper/Constants";
import { ReactComponent as IconSearch } from "../../../assets/icons/icon-search.svg";
import { ReactComponent as IconTransfer } from "../../../assets/icons/icon-action-transfer.svg";
import { ReactComponent as IconReport } from "../../../assets/icons/icon-action-report.svg";
import { ReactComponent as IconChangePassword } from "../../../assets/icons/icon-action-change-password.svg";
import CustomTable from "../../../components/ui/tables/CustomTable";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import OnlineUserChangePassword from "./OnlineUserChangePassword";
import UserStatusChangeConfirmDialog from "./UserStatusChangeConfirmDialog";
import { useNavigate } from "react-router-dom";
import SelectDropDown from "../../../components/ui/forms/SelectDropDown";
import { FaUserTimes } from "react-icons/fa";
import SectionLoader from "components/ui/loaders/SectionLoader";
import Auth from "service/AuthService";
import { useAuth } from "provider/AuthContext";

// Debounce helper
const debouncePromise = (fn, delay = 300) => {
  let timer;
  let resolvers = [];
  return (...args) =>
    new Promise((resolve) => {
      if (timer) clearTimeout(timer);
      resolvers.push(resolve);

      timer = setTimeout(async () => {
        try {
          const result = await fn(...args);
          resolvers.forEach((r) => r(result));
        } finally {
          resolvers = [];
        }
      }, delay);
    });
};

const OnlineUser = () => {
  const userService = new UserService(API_URL);
  const authServ = useMemo(() => new Auth(API_URL), []);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [allUsersBalance, setAllUsersBalance] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortCriteria, setSortCriteria] = useState("alphabetically");
  const [userId, setUserId] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [userIdForPasswordUpdate, setUserIdForPasswordUpdate] = useState("");
  const [openUserStatusChangeConfirmModal, setOpenUserStatusChangeConfirmModal] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState({});
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false);

  const debouncedFetchUsers = useMemo(
    () =>
      debouncePromise(async (inputValue) => {
        if (inputValue.length < 3) return [];

        try {
          if (user?.role !== "Owner") return [];

          const response = await authServ.getAllUsersWithSearchText({
            searchText: inputValue,
            registrationType: "online",
          });

          const options = response.users?.map((item) => ({
            value: item._id,
            label: item.username,
          })) || [];

          return options;
        } catch (error) {
          console.error("Error fetching users:", error);
          return [];
        }
      }, 1000),
    [authServ, user?.role]
  );

  const handleInputChange = (value) => {
    setSearchTerm(value);
  };

  const handleMenuClose = () => {};

  useEffect(() => {
    if (!user || user.role !== "Owner") {
      navigate(-1);
      toast.error("You don't have permission to access this page");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (hasSearchedOnce) fetchUserBalance();
  }, [pagination]);

  const fetchUserBalance = async () => {
    setLoading(true);
    try {
      const queryParams = {};
      const onlineUserName = selectedUser?.label || "";

      if (onlineUserName) queryParams.username = onlineUserName;
      if (userId) queryParams.userId = userId;

      const data = await userService.getOnlineUserBalance(queryParams);
      const formattedData = Array.isArray(data) ? data : [data];

      setAllUsersBalance(formattedData);
      setTotalCount(formattedData.length);
      setHasSearchedOnce(true);
    } catch (error) {
      setResponseMsg(error.message || "Failed to fetch user data");
      console.error("Error fetching users:", error);
      toast.error("Error fetching user data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ pageIndex: 0, pageSize: 10 });
    fetchUserBalance();
  };

  const handleReset = () => {
    setSelectedUser(null);
    setUserId("");
    setAllUsersBalance([]);
    setResponseMsg("");
    setSearchTerm("");
    setHasSearchedOnce(false);
  };

  const sortedUsers = useMemo(() => {
    if (!allUsersBalance || !allUsersBalance.length) return [];
    return [...allUsersBalance].sort((a, b) => {
      if (sortCriteria === "alphabetically") return a.name.localeCompare(b.name);
      if (sortCriteria === "balance") return b.balance - a.balance;
      return 0;
    });
  }, [allUsersBalance, sortCriteria]);

  const tableData = sortedUsers;
  const totalUsers = sortedUsers.length;
  const totalBal = sortedUsers.reduce((acc, row) => acc + row.balance, 0);
  const totalNetworkBal = sortedUsers.reduce((acc, row) => acc + row.networkBalance, 0);

  const tableColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "User",
        size: 150,
        Cell: ({ cell }) => <div>{cell.row.original.name}</div>,
        Footer: () => <>Total ({totalUsers} players)</>,
      },
      { accessorKey: "role", header: "Role", size: 80 },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <div className={`font-medium ${cell.row.original.status === "active" ? "text-green-600" : "text-red-600"}`}>
            {cell.row.original.status || "unknown"}
          </div>
        ),
      },
      {
        accessorKey: "balance",
        header: "Balance",
        size: 50,
        Cell: ({ cell }) => (
          <>{formatToTwoDecimalPlaces(cell.row.original.balance || 0)} {user?.currency}</>
        ),
        Footer: () => <>{formatToTwoDecimalPlaces(totalBal)} {user?.currency}</>,
      },
      {
        accessorKey: "networkBalance",
        header: "Network Balance",
        size: 50,
        Cell: ({ cell }) => (
          <>{formatToTwoDecimalPlaces(cell.row.original.networkBalance || 0)} {user?.currency}</>
        ),
        Footer: () => <>{formatToTwoDecimalPlaces(totalNetworkBal)} {user?.currency}</>,
      },
      {
  accessorKey: "#",
  header: "Actions",
  size: 50,
  Cell: ({ cell }) => (
    <div className="flex space-x-1">
      {/* Transfer - First */}
      <span className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] text-md text-[#242424] rounded-lg bg-white border border-black mx-1 py-1">
        <IconTransfer /> <span>Transfer</span>
      </span>

      {/* Change Status - Second */}
      <span
        className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] rounded-lg bg-white border border-black mx-1 py-1"
        onClick={() => {
          setOpenUserStatusChangeConfirmModal(true);
          setSelectedUserData(cell.row.original);
        }}
      >
        <FaUserTimes /> <span>{t("Change Status")}</span>
      </span>

   
      <span className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] rounded-lg bg-white border border-black mx-1 py-1">
        <IconReport /> <span>Report</span>
      </span>

    
      <span
        className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] rounded-lg bg-white border border-black mx-1 py-1"
        onClick={() => {
          setOpenChangePasswordModal(true);
          setUserIdForPasswordUpdate(cell.row.original.id);
        }}
      >
        <IconChangePassword /> <span>{t("Change Password")}</span>
      </span>
    </div>
  ),
}

    ],
    [totalBal, totalNetworkBal, totalUsers, t, user]
  );

  return (
    <div className="flex flex-col w-full">
      <header className="rounded-lg bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">
          Manage Online Users
        </h1>
      </header>

      <div className="flex flex-col md:flex-row gap-3 mt-5">
        <div className="w-full md:w-7/3 text-black ">
          <SelectDropDown
            label="Choose User"
            placeholder={"Type 3+ letters"}
            selectedOption={selectedUser || []}
            loadOptions={(inputValue, callback) =>
              debouncedFetchUsers(inputValue).then(callback).catch(() => callback([]))
            }
            handleChange={(option) => setSelectedUser(option)}
            inputValue={searchTerm || ""}
            onInputChange={handleInputChange}
            menuIsOpen={searchTerm.length >= 3}
            onMenuClose={handleMenuClose}
            isSearchable={true}
          />
        </div>
        <div className="w-full md:w-7/3">
          <label className="block text-md ml-3 text-[#242424]">User ID</label>
          <input
            name="userId"
            id="userId"
            autoComplete="off"
            className="text-black w-full rounded-md border border-[#ccc] px-2 mt-[0.4px] py-1.5"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-3 mt-5">
        <button
          className="bg-[#f2c41a] hover:bg-[#f2c41a]/80 text-black w-full md:w-36 font-bold py-2 rounded-lg"
          type="button"
          onClick={handleSearch}
        >
          SEARCH
        </button>
        <button
          className="text-center border border-black w-full md:w-36 rounded-lg text-md hover:bg-gray-300 text-gray-700 font-bold py-2 px-4"
          type="reset"
          onClick={handleReset}
        >
          RESET
        </button>
      </div>

      <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />

      {loading ? (
        <div className="flex justify-center py-8">
          <SectionLoader bgColor="light" />
        </div>
      ) : allUsersBalance?.length ? (
        <>
          <div className="mb-4 flex items-center">
            <span className="text-sm text-black">Sort by:</span>
            <div className="flex ml-2">
              <label htmlFor="sortByUser" className="space-x-2 pr-3">
                <input
                  type="radio"
                  name="sortCriteria"
                  id="sortByUser"
                  value="alphabetically"
                  checked={sortCriteria === "alphabetically"}
                  onChange={(e) => setSortCriteria(e.target.value)}
                />
                <span className="text-black font-semibold">Alphabetically</span>
              </label>
              <label htmlFor="sortByBalance" className="space-x-2">
                <input
                  type="radio"
                  name="sortCriteria"
                  id="sortByBalance"
                  value="balance"
                  checked={sortCriteria === "balance"}
                  onChange={(e) => setSortCriteria(e.target.value)}
                />
                <span className="text-black font-semibold">Balance</span>
              </label>
            </div>
          </div>

          <CustomTable
            enableStickyFooter
            columns={tableColumns}
            data={tableData}
            enableGlobalFilter={true}
            enableFilterMatchHighlighting={true}
            setPagination={setPagination}
            pagination={pagination}
            totalCount={totalCount}
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
          <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
            <IconSearch />
          </div>
          <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
            {responseMsg || "Enter a Username or User ID to search"}
          </span>
        </div>
      )}

      {openChangePasswordModal && (
        <OnlineUserChangePassword
          isOpen={openChangePasswordModal}
          onClose={() => setOpenChangePasswordModal(false)}
          userIdForPasswordUpdate={userIdForPasswordUpdate}
          setUserIdForPasswordUpdate={setUserIdForPasswordUpdate}
        />
      )}

      {openUserStatusChangeConfirmModal && (
        <UserStatusChangeConfirmDialog
          isOpen={openUserStatusChangeConfirmModal}
          onClose={() => setOpenUserStatusChangeConfirmModal(false)}
          selectedUserData={selectedUserData}
          setSelectedUserData={setSelectedUserData}
          onSuccess={() => {
            toast.success(`Status changed successfully`);
            fetchUserBalance();
          }}
        />
      )}
    </div>
  );
};

export default OnlineUser;
